import React, { useState, useEffect } from 'react';
import { motivationService } from '../../services/motivationService';
import { useLanguage } from '../../contexts/LanguageContext';
import { Plus, Edit, Trash2, Eye, EyeOff, BarChart3, MessageCircle } from 'lucide-react';

const MessageList = ({ onAddMessage, onEditMessage }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const { t } = useLanguage();

    useEffect(() => {
        loadMessages();
    }, [currentPage, statusFilter]);

    const loadMessages = async () => {
        try {
            setLoading(true);
            const response = await motivationService.getAllMessages(currentPage, 20, statusFilter);
            if (response.success) {
                setMessages(response.data.messages || []);
                setTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (error) {
            console.error('Mesajlar yüklenirken hata:', error);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (window.confirm(t('motivationDeleteConfirm'))) {
            try {
                await motivationService.deleteMessage(messageId);
                loadMessages();
            } catch (error) {
                console.error('Mesaj silinirken hata:', error);
            }
        }
    };

    const handleToggleStatus = async (message) => {
        try {
            await motivationService.toggleMessageStatus(message.id, !message.is_active);
            loadMessages();
        } catch (error) {
            console.error('Mesaj durumu değiştirilirken hata:', error);
        }
    };

    const getStats = () => {
        const total = messages.length;
        const active = messages.filter(m => m.is_active).length;
        const inactive = total - active;
        return { total, active, inactive };
    };

    const stats = getStats();

    if (loading) {
        return (
            <div className="motivation-loading">
                <div className="loading-spinner"></div>
                <div>{t('loading')}</div>
            </div>
        );
    }

    return (
        <div className="message-list-container">
            {/* İstatistik Kartları */}
            <div className="motivation-stats">
                <div className="stat-card motivation-stat-total">
                    <div className="stat-icon">
                        <MessageCircle size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>{stats.total}</h3>
                        <p>{t('motivationTotalCount')}</p>
                    </div>
                </div>
                <div className="stat-card motivation-stat-active">
                    <div className="stat-icon">
                        <Eye size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>{stats.active}</h3>
                        <p>{t('motivationActiveCount')}</p>
                    </div>
                </div>
                <div className="stat-card motivation-stat-inactive">
                    <div className="stat-icon">
                        <EyeOff size={24} />
                    </div>
                    <div className="stat-content">
                        <h3>{stats.inactive}</h3>
                        <p>{t('motivationInactiveCount')}</p>
                    </div>
                </div>
            </div>

            {/* Aksiyon Bar */}
            <div className="motivation-actions">
                <div className="actions-left">
                    <h2>{t('motivationMessages')}</h2>
                </div>
                <div className="actions-right">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="status-filter"
                    >
                        <option value="">{t('allStatuses')}</option>
                        <option value="active">{t('active')}</option>
                        <option value="inactive">{t('inactive')}</option>
                    </select>
                    <button
                        className="add-message-btn"
                        onClick={onAddMessage}
                    >
                        <Plus size={16} />
                        {t('motivationAddNew')}
                    </button>
                </div>
            </div>

            {/* Mesajlar Grid */}
            <div className="messages-grid">
                {messages.map(message => (
                    <div key={message.id} className="message-card">
                        <div className="message-header">
                            <h3 className="message-title">{message.title}</h3>
                            <div className="message-actions">
                                <button
                                    className={`status-btn ${message.is_active ? 'active' : 'inactive'}`}
                                    onClick={() => handleToggleStatus(message)}
                                    title={message.is_active ? t('motivationDeactivate') : t('motivationActivate')}
                                >
                                    {message.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                                </button>
                                <button
                                    className="edit-btn"
                                    onClick={() => onEditMessage(message)}
                                    title={t('motivationEditMessage')}
                                >
                                    <Edit size={14} />
                                </button>
                                <button
                                    className="delete-btn"
                                    onClick={() => handleDeleteMessage(message.id)}
                                    title={t('motivationDelete')}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>

                        <div className="message-content">
                            {message.content}
                        </div>

                        <div className="message-footer">
                            <span className={`status-badge ${message.is_active ? 'active' : 'inactive'}`}>
                                {message.is_active ? t('active') : t('inactive')}
                            </span>
                            <span className="message-date">
                                {new Date(message.created_at).toLocaleDateString('tr-TR')}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {messages.length === 0 && !loading && (
                <div className="motivation-empty">
                    <MessageCircle size={48} />
                    <p>{t('motivationNoMessages')}</p>
                    <button
                        className="add-first-message"
                        onClick={onAddMessage}
                    >
                        {t('motivationAddNew')}
                    </button>
                </div>
            )}

            {/* Sayfalama */}
            {totalPages > 1 && (
                <div className="motivation-pagination">
                    <div className="pagination-info">
                        {t('page')} {currentPage} / {totalPages}
                    </div>
                    <div className="pagination-controls">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                        >
                            {t('previous')}
                        </button>
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                        >
                            {t('next')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageList;